import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, update, get } from 'firebase/database';
import { db, database } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import {
  INSTITUCIONES,
  INSTITUCION_SEMAR,
  getCatalog,
  getInstitution,
  normalizeInstitucion,
} from '../config/institutions';
import '../styles/Onboarding.css';

const OTRO = 'OTRO (Especificar)...';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, profileData, profileComplete } = useAuth();

  const [step, setStep] = useState(0);
  const [institucion, setInstitucion] = useState('');
  const [grado, setGrado] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [especialidad, setEspecialidad] = useState('');
  const [otroCuerpo, setOtroCuerpo] = useState(false);
  const [customCuerpo, setCustomCuerpo] = useState('');
  const [otraEspecialidad, setOtraEspecialidad] = useState(false);
  const [customEspecialidad, setCustomEspecialidad] = useState('');
  const [semarCatalog, setSemarCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const snapshot = await get(ref(database, 'app_metadata'));
        if (!active || !snapshot.exists()) return;
        const data = snapshot.val();
        const grados = data.grados ? Object.values(data.grados) : [];
        const cuerpos = data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [];
        const especialidades = data.especialidades || {};
        setSemarCatalog({
          grados: grados.length > 0 ? grados : getCatalog(INSTITUCION_SEMAR).grados,
          cuerpos: cuerpos.length > 0 ? cuerpos : getCatalog(INSTITUCION_SEMAR).cuerpos,
          especialidades: Object.keys(especialidades).length > 0
            ? especialidades
            : getCatalog(INSTITUCION_SEMAR).especialidades,
        });
      } catch (err) {
        console.warn('[Onboarding] No se pudo cargar app_metadata de SEMAR:', err?.message || err);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!profileData) return;
    const inst = normalizeInstitucion(profileData.institucion);
    if (inst) setInstitucion(inst);
    const existingGrado = profileData.grado || profileData.jerarquia || profileData.hierarchy || '';
    if (existingGrado) setGrado(existingGrado);
    const existingCuerpo = profileData.cuerpoServicio || profileData.cuerpo || profileData.corps || '';
    if (existingCuerpo) setCuerpo(existingCuerpo);
    const existingEspecialidad = profileData.especialidad || profileData.specialty || '';
    if (existingEspecialidad) setEspecialidad(existingEspecialidad);
  }, [profileData]);

  useEffect(() => {
    if (saved && profileComplete) {
      navigate('/dashboard', { replace: true });
    }
  }, [saved, profileComplete, navigate]);

  const institution = getInstitution(institucion);
  const catalog = useMemo(() => {
    if (institucion === INSTITUCION_SEMAR) {
      return semarCatalog || getCatalog(INSTITUCION_SEMAR);
    }
    return getCatalog(institucion);
  }, [institucion, semarCatalog]);

  const esSemar = institucion === INSTITUCION_SEMAR;
  const cuerpos = useMemo(
    () => (esSemar ? [...catalog.cuerpos, OTRO] : catalog.cuerpos),
    [catalog.cuerpos, esSemar]
  );
  const especialidades = useMemo(() => {
    const list = catalog.especialidades?.[cuerpo] || [];
    if (!cuerpo || cuerpo === OTRO) return [];
    if (!esSemar) return list;
    return list.length > 0 ? [...list, OTRO] : ['TODOS', OTRO];
  }, [catalog.especialidades, cuerpo, esSemar]);

  const selectInstitution = (id) => {
    setInstitucion(id);
    setGrado('');
    setCuerpo('');
    setEspecialidad('');
    setOtroCuerpo(false);
    setCustomCuerpo('');
    setOtraEspecialidad(false);
    setCustomEspecialidad('');
    setError('');
  };

  const handleCuerpoChange = (value) => {
    setCuerpo(value);
    setEspecialidad('');
    setOtraEspecialidad(false);
    setCustomEspecialidad('');
    setOtroCuerpo(esSemar && value === OTRO);
  };

  const handleEspecialidadChange = (value) => {
    setEspecialidad(value);
    setOtraEspecialidad(esSemar && value === OTRO);
  };

  const finalCuerpo = otroCuerpo ? customCuerpo.trim() : cuerpo;
  const finalEspecialidad = otraEspecialidad ? customEspecialidad.trim() : especialidad;
  const stepTwoValid = Boolean(
    grado &&
    finalCuerpo &&
    finalEspecialidad &&
    finalCuerpo !== OTRO &&
    finalEspecialidad !== OTRO
  );

  const handleFinish = async () => {
    if (!user || !stepTwoValid) return;
    setSaving(true);
    setError('');

    const payload = {
      uid: user.uid,
      email: user.email || profileData?.email || '',
      nick: profileData?.nick || user.displayName || '',
      institucion,
      grado,
      jerarquia: grado,
      cuerpoServicio: finalCuerpo,
      especialidad: finalEspecialidad,
      onboardingCompleted: true,
      updatedAt: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, 'users', user.uid), payload, { merge: true });

      // Espejo RTDB para que la app móvil lea el perfil sin re-onboarding.
      try {
        await update(ref(database, `users/${user.uid}`), {
          uid: user.uid,
          email: payload.email,
          nick: payload.nick,
          institucion,
          grado,
          jerarquia: grado,
          cuerpoServicio: finalCuerpo,
          especialidad: finalEspecialidad,
          onboardingCompleted: true,
          updatedAt: Date.now(),
        });
      } catch (rtdbError) {
        console.warn('[Onboarding] Firestore guardado; no se pudo espejar en RTDB:', rtdbError?.message || rtdbError);
      }

      setSaved(true);
    } catch (err) {
      console.error('[Onboarding] Error guardando perfil:', err);
      setError('No se pudo guardar tu perfil. Verifica tu conexión e inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div
      className="onboarding-page"
      style={{
        '--onboarding-accent': institution.accent,
        '--onboarding-accent-light': institution.accentLight,
      }}
    >
      <div className="onboarding-shell">
        <div className="onboarding-card">
          <div className="onboarding-topline">
            <div className="onboarding-brand">
              <span className="onboarding-brand-mark">{institution.emoji}</span>
              <span>Vortex</span>
            </div>
            <div className="onboarding-steps" aria-label="Progreso del onboarding">
              <span className={`onboarding-step-dot ${step === 0 ? 'is-active' : 'is-done'}`}>1</span>
              <span className={`onboarding-step-dot ${step === 1 ? 'is-active' : ''}`}>2</span>
            </div>
          </div>

          {error && <div className="onboarding-error" role="alert">⚠️ {error}</div>}

          {step === 0 && (
            <>
              <h1 className="onboarding-heading">Selecciona tu institución</h1>
              <p className="onboarding-subheading">
                Tu contenido, jerarquías y simuladores se ajustarán a la institución que elijas.
                Esta selección se guarda en tu perfil y también queda disponible para la app móvil.
              </p>

              <div className="onboarding-institutions">
                {INSTITUCIONES.map((inst) => {
                  const selected = institucion === inst.id;
                  return (
                    <button
                      key={inst.id}
                      type="button"
                      className={`onboarding-institution ${selected ? 'is-selected' : ''}`}
                      style={{ '--inst-accent': inst.accent }}
                      onClick={() => selectInstitution(inst.id)}
                      aria-pressed={selected}
                    >
                      <span className="onboarding-institution-icon">{inst.emoji}</span>
                      <span className="onboarding-institution-text">
                        <strong>{inst.nombre}</strong>
                        <span>{inst.subtitulo}</span>
                      </span>
                      {selected && <span className="onboarding-institution-check">✓</span>}
                    </button>
                  );
                })}
              </div>

              <div className="onboarding-actions">
                <button
                  type="button"
                  className="onboarding-btn primary"
                  disabled={!institucion}
                  onClick={() => setStep(1)}
                >
                  Continuar
                </button>
              </div>
              <p className="onboarding-note">
                Usuarios con perfil creado en la app móvil no necesitan repetir este paso.
              </p>
            </>
          )}

          {step === 1 && (
            <>
              <button
                type="button"
                className="onboarding-institution-chip"
                onClick={() => setStep(0)}
              >
                <span>{institution.emoji}</span>
                <span>{institution.nombre}</span>
                <span>· Cambiar</span>
              </button>

              <h1 className="onboarding-heading">Configura tu perfil</h1>
              <p className="onboarding-subheading">{institution.terminology.groupPrompt}</p>

              <div className="onboarding-form">
                <div className="onboarding-field">
                  <label htmlFor="onboarding-grado">{institution.terminology.hierarchyLabel}</label>
                  <select
                    id="onboarding-grado"
                    value={grado}
                    onChange={(e) => setGrado(e.target.value)}
                  >
                    <option value="">Selecciona una opción</option>
                    {catalog.grados.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="onboarding-field">
                  <label htmlFor="onboarding-cuerpo">{institution.terminology.groupLabel}</label>
                  <select
                    id="onboarding-cuerpo"
                    value={cuerpo}
                    onChange={(e) => handleCuerpoChange(e.target.value)}
                  >
                    <option value="">Selecciona una opción</option>
                    {cuerpos.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {otroCuerpo && (
                  <div className="onboarding-field">
                    <label htmlFor="onboarding-cuerpo-otro">Especifica tu {institution.terminology.groupLabel}</label>
                    <input
                      id="onboarding-cuerpo-otro"
                      type="text"
                      value={customCuerpo}
                      onChange={(e) => setCustomCuerpo(e.target.value)}
                      placeholder="Escribe tu cuerpo o servicio"
                    />
                  </div>
                )}

                <div className="onboarding-field">
                  <label htmlFor="onboarding-especialidad">{institution.terminology.specialtyLabel}</label>
                  <select
                    id="onboarding-especialidad"
                    value={especialidad}
                    onChange={(e) => handleEspecialidadChange(e.target.value)}
                    disabled={!cuerpo || cuerpo === OTRO}
                  >
                    <option value="">
                      {!cuerpo || cuerpo === OTRO
                        ? institution.terminology.groupRequiredPrompt
                        : institution.terminology.specialtyPlaceholder}
                    </option>
                    {especialidades.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {otraEspecialidad && (
                  <div className="onboarding-field">
                    <label htmlFor="onboarding-especialidad-otra">Especifica tu especialidad</label>
                    <input
                      id="onboarding-especialidad-otra"
                      type="text"
                      value={customEspecialidad}
                      onChange={(e) => setCustomEspecialidad(e.target.value)}
                      placeholder="Escribe tu especialidad"
                    />
                  </div>
                )}
              </div>

              <div className="onboarding-actions">
                <button
                  type="button"
                  className="onboarding-btn secondary"
                  onClick={() => setStep(0)}
                  disabled={saving}
                >
                  Atrás
                </button>
                <button
                  type="button"
                  className="onboarding-btn primary"
                  onClick={handleFinish}
                  disabled={!stepTwoValid || saving}
                >
                  {saving ? 'Guardando...' : 'Finalizar configuración'}
                </button>
              </div>
              <p className="onboarding-note">
                Podrás consultar y actualizar estos datos desde tu perfil. El contenido de otras
                instituciones se activará cuando su banco de preguntas esté disponible.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
